import { ref } from 'vue'
import { ElMessage } from 'element-plus'

// 精简的配置接口定义
export interface ImageUploadConfig {
  /** 上传接口地址 */
  uploadAction?: string
  /** 允许的文件类型，默认 ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'] */
  allowedTypes?: string[]
  /** 文件大小限制（MB），默认 5MB */
  maxSizeInMB?: number
}

export function useImageUpload(config: ImageUploadConfig = {}) {
  const finalConfig = { ...config }
  // 文件上传相关配置
  const uploadRef = ref<any>(null)
  const uploadAction = ref<string>(finalConfig.uploadAction || '')
  const uploadHeaders = ref<Record<string, string>>({})
  const uploadData = ref<Record<string, any>>({})

  // 验证配置
  const allowedTypes = ref<string[]>(
    finalConfig.allowedTypes || ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  )
  const maxSizeInMB = ref<number>(finalConfig.maxSizeInMB || 5)

  // 当前选中的图片元素
  const currentImageTarget = ref<any>(null)

  /**
   * 触发文件选择
   */
  const triggerFileUpload = () => {
    if (uploadRef.value && uploadRef.value.$el) {
      const input = uploadRef.value.$el.querySelector('input[type="file"]')
      if (input) {
        input.click()
      }
    }
  }

  /**
   * 上传前的验证
   */
  const beforeUpload = (file: File) => {
    // 检查文件类型
    const isValidType = allowedTypes.value.includes(file.type)
    if (!isValidType) {
      ElMessage.error(
        `只能上传图片文件！支持格式：${allowedTypes.value.map((type) => type.replace('image/', '')).join('、')}`,
      )
      console.warn(`不支持的文件类型: ${file.type}，支持的类型: ${allowedTypes.value.join(', ')}`)
      return false
    }

    // 检查文件大小
    const fileSizeInMB = file.size / 1024 / 1024
    if (fileSizeInMB > maxSizeInMB.value) {
      ElMessage.error(`图片大小不能超过 ${maxSizeInMB.value}MB！`)
      console.warn(`文件大小超限: ${fileSizeInMB.toFixed(2)}MB > ${maxSizeInMB.value}MB`)
      return false
    }

    console.log('准备上传文件:', {
      name: file.name,
      type: file.type,
      size: `${fileSizeInMB.toFixed(2)}MB`,
    })
    return true
  }

  /**
   * 上传成功回调
   */
  const onUploadSuccess = (response: any, uploadFile: any) => {
    console.log('上传成功:', response, uploadFile)

    try {
      // 根据接口返回的数据结构调整，支持多种常见格式
      let imageUrl = ''

      // 支持的响应格式：
      // 1. { success: true, data: { url: "xxx" } }
      // 2. { code: 200, data: { url: "xxx" } }
      // 3. { url: "xxx" }
      // 4. "http://xxx.jpg" (直接返回URL字符串)
      if (response.data && response.data.url) {
        imageUrl = response.data.url
      } else if (response.url) {
        imageUrl = response.url
      } else if (typeof response === 'string' && response.startsWith('http')) {
        imageUrl = response
      } else if (
        response.data &&
        typeof response.data === 'string' &&
        response.data.startsWith('http')
      ) {
        imageUrl = response.data
      }

      if (imageUrl) {
        // 设置图片到当前选中的图片元素
        if (currentImageTarget.value) {
          currentImageTarget.value.refresh(imageUrl, {
            auto: true, // 根据图片宽高自动等比(宽>高?width:height)
            // width: true, // 按宽调整高
            // height: true, // 按高调整宽
            // real: true // 根据图片实际尺寸调整(转pt)
          })
          currentImageTarget.value = null // 清空引用
        }
        ElMessage.success('图片上传成功')
        console.log('设置图片URL:', imageUrl)
      } else {
        console.warn('无法解析图片URL，响应数据:', response)
        ElMessage.error('上传响应格式错误，请检查接口返回数据')
      }
    } catch (error) {
      console.error('处理上传结果失败:', error)
      ElMessage.error('处理上传结果失败')
    }
  }

  /**
   * 上传失败回调
   */
  const onUploadError = (error: any) => {
    console.error('上传失败:', error)
    ElMessage.error('图片上传失败，请重试')
  }

  /**
   * 处理图片选择点击事件
   * @param target 当前选中的图片元素
   */
  const handleImageChooseClick = (target: any) => {
    if (!uploadAction.value) {
      ElMessage.error('uploadAction is required, please check the props')
      return
    }
    console.log('onImageChooseClick', target)
    // 保存当前图片元素的引用
    currentImageTarget.value = target
    // 触发文件选择
    triggerFileUpload()
  }

  /**
   * 重置上传状态
   */
  const resetUploadState = () => {
    currentImageTarget.value = null
  }

  return {
    // 响应式数据
    uploadRef,
    uploadAction,
    uploadHeaders,
    uploadData,

    // 方法
    triggerFileUpload,
    beforeUpload,
    onUploadSuccess,
    onUploadError,
    handleImageChooseClick,
    resetUploadState,
  }
}
